import React, { useState, useEffect, useRef } from 'react';
import { Home, MessageCircle, User, Users, X, CalendarDays, Library as LibraryIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import { slugify } from '../lib/spaceSlug';
import SpaceRow from './SpaceRow';
import { useEventsEnabled } from '../hooks/useEventsEnabled';
import { useLibraryEnabled } from '../hooks/useLibraryEnabled';

const MobileNav = ({ onNavigate, currentPage }) => {
  const { profile } = useAuth();
  const eventsEnabled = useEventsEnabled();
  const showEvents = eventsEnabled === true || profile?.is_admin === true;
  const libraryEnabled = useLibraryEnabled();
  const showLibrary = libraryEnabled === true || profile?.is_admin === true;
  const isEventsActive = currentPage === 'calendar' || currentPage.startsWith('event:');
  const [groups, setGroups] = useState([]);
  const [showSpaces, setShowSpaces] = useState(false);
  const dialogRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    api.getGroups()
      .then((res) => setGroups(res.data || []))
      .catch((err) => console.error('Error fetching groups:', err));
  }, []);

  // Focus trap + Escape for the Spaces dialog
  useEffect(() => {
    if (!showSpaces) return undefined;
    const dialog = dialogRef.current;
    const focusable = () => dialog.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])');
    focusable()[0]?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { setShowSpaces(false); return; }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    const opener = openerRef.current;
    return () => { document.removeEventListener('keydown', onKeyDown); opener?.focus(); };
  }, [showSpaces]);

  const isMessagesActive = currentPage === 'messages' || currentPage.startsWith('messages:');
  const isProfileActive = currentPage.startsWith('user:') && currentPage === `user:${profile?.display_name}`;

  return (
    <>
      {/* Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 px-4 pt-2 safe-area-pb safe-area-x">
        <div className="flex items-center justify-around">

          {/* Home */}
          <button
            onClick={() => onNavigate('feed')}
            className={`flex flex-col items-center p-2 ${currentPage === 'feed' ? 'text-accent-teal' : 'text-text-muted'}`}
            data-testid="mobile-nav-home"
          >
            <Home className="w-6 h-6" />
            <span className="text-xs mt-1">Home</span>
          </button>

          {/* Spaces */}
          <button
            ref={openerRef}
            onClick={() => setShowSpaces(true)}
            aria-haspopup="dialog"
            className={`flex flex-col items-center p-2 min-h-[44px] min-w-[44px] ${currentPage.startsWith('space:') ? 'text-accent-teal' : 'text-text-muted'}`}
            data-testid="mobile-nav-groups"
          >
            <Users className="w-6 h-6" aria-hidden="true" />
            <span className="text-xs mt-1">Spaces</span>
          </button>

          {/* Events */}
          {showEvents && (
            <button
              onClick={() => onNavigate('calendar')}
              className={`flex flex-col items-center p-2 min-h-[44px] min-w-[44px] ${isEventsActive ? 'text-accent-teal' : 'text-text-muted'}`}
              data-testid="mobile-nav-events"
            >
              <CalendarDays className="w-6 h-6" aria-hidden="true" />
              <span className="text-xs mt-1">Events</span>
            </button>
          )}

          {/* Messages */}
          <button
            onClick={() => onNavigate('messages')}
            className={`flex flex-col items-center p-2 ${isMessagesActive ? 'text-accent-teal' : 'text-text-muted'}`}
            data-testid="mobile-nav-messages"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="text-xs mt-1">Messages</span>
          </button>

          {/* Profile — navigates to the user's own public profile page */}
          <button
            onClick={() => profile?.display_name && onNavigate(`user:${profile.display_name}`)}
            className={`flex flex-col items-center p-2 ${isProfileActive ? 'text-accent-teal' : 'text-text-muted'}`}
            data-testid="mobile-nav-profile"
          >
            <User className="w-6 h-6" />
            <span className="text-xs mt-1">Profile</span>
          </button>

        </div>
      </nav>

      {showSpaces && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setShowSpaces(false)}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-spaces-title"
            className="absolute bottom-0 left-0 right-0 bg-background rounded-t-arch-xl p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 id="mobile-spaces-title" className="font-heading text-xl font-semibold text-text-main">
                Community Spaces
              </h2>
              <button
                onClick={() => setShowSpaces(false)}
                aria-label="Close spaces menu"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-card rounded-full"
                data-testid="close-groups-modal"
              >
                <X className="w-5 h-5 text-text-muted" aria-hidden="true" />
              </button>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => { onNavigate('feed'); setShowSpaces(false); }}
                className={`w-full flex items-center gap-3 p-4 rounded-arch-lg transition-all ${
                  currentPage === 'feed' ? 'sidebar-active bg-primary/10' : 'hover:bg-primary/5'
                }`}
                data-testid="mobile-all-posts"
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" aria-hidden="true" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-text-main">All Posts</p>
                  <p className="text-sm text-text-muted">Main community feed</p>
                </div>
              </button>

              {groups.map((group) => (
                <SpaceRow
                  key={group.id}
                  group={group}
                  isActive={currentPage === `space:${group.id}` || currentPage === `space:${slugify(group.name)}`}
                  canOpen={profile?.is_admin === true}
                  onOpen={(g) => { onNavigate(`space:${slugify(g.name)}`); setShowSpaces(false); }}
                  testIdPrefix="mobile-group"
                  wrapText
                />
              ))}

              {showLibrary && (
                <button
                  onClick={() => { onNavigate('library'); setShowSpaces(false); }}
                  className={`w-full flex items-center gap-3 p-4 rounded-arch-lg transition-all ${currentPage === 'library' ? 'sidebar-active bg-primary/10' : 'hover:bg-primary/5'}`}
                  data-testid="mobile-library-link"
                >
                  <div className="w-10 h-10 rounded-full bg-lavender/20 flex items-center justify-center">
                    <LibraryIcon className="w-5 h-5 text-lavender-dark" aria-hidden="true" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-text-main">Library</p>
                    <p className="text-sm text-text-muted">Guides, tools and resources</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNav;
